import '@fontsource/open-sans' // Defaults to weight 400 with normal variant.

import i18n from 'i18next'
import {
    initReactI18next,
    I18nextProvider,
    useTranslation,
} from 'react-i18next'
import React, { useEffect } from 'react'
import enUSTranslation from './src/locales/en_US/translation.json'
import esARTranslation from './src/locales/es_AR/translation.json'
import es419Translation from './src/locales/es_419/translation.json'
import esUSTranslation from './src/locales/es_US/translation.json'
import esESTranslation from './src/locales/es_ES/translation.json'
import esCLTranslation from './src/locales/es_CL/translation.json'
import esCOTranslation from './src/locales/es_CO/translation.json'
import esMXTranslation from './src/locales/es_MX/translation.json'
import esVETranslation from './src/locales/es_VE/translation.json'
import esUYTranslation from './src/locales/es_UY/translation.json'
import esPETranslation from './src/locales/es_PE/translation.json'
import ptBRTranslation from './src/locales/pt_BR/translation.json'
import ptPTTranslation from './src/locales/pt_PT/translation.json'
import itITTranslation from './src/locales/it_IT/translation.json'
import deDETranslation from './src/locales/de_DE/translation.json'
import frFRTranslation from './src/locales/fr_FR/translation.json'
import enUKTranslation from './src/locales/en_UK/translation.json'
import zhCNTranslation from './src/locales/zh_CN/translation.json'
import hiINTranslation from './src/locales/hi_IN/translation.json'
import koKRTranslation from './src/locales/ko_KR/translation.json'
import jaJPTranslation from './src/locales/ja_JP/translation.json'
import zhTWTranslation from './src/locales/zh_TW/translation.json'
import arSATranslation from './src/locales/ar_SA/translation.json'
import arAETranslation from './src/locales/ar_AE/translation.json'
import enSGTranslation from './src/locales/en_SG/translation.json'
import { ThemeProvider } from '@mui/material/styles'
import theme from './theme'
import { QueryClientProvider } from '@tanstack/react-query'
import queryClient from './src/services/queryClient'
import { TourProvider } from '@reactour/tour'
import tourStyles from './src/components/Tour'
import { createDevice } from './src/services/device'
import '@ecolell/pwa-install'
import './src/components/Layout.css'
import './src/services/sentry'
import { Box, IconButton } from '@mui/material'
import DoneIcon from '@mui/icons-material/Done'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { getStoredLocale, resolveAcceptedLocale } from './src/services/language'
import AnnaTourContent from './src/components/AnnaTourContent'

const resources = {
    'en-US': {
        translation: enUSTranslation,
    },
    'es-AR': {
        translation: esARTranslation,
    },
    'es-ES': {
        translation: esESTranslation,
    },
    'es-419': {
        translation: es419Translation,
    },
    'es-US': {
        translation: esUSTranslation,
    },
    'es-CL': {
        translation: esCLTranslation,
    },
    'es-CO': {
        translation: esCOTranslation,
    },
    'es-MX': {
        translation: esMXTranslation,
    },
    'es-VE': {
        translation: esVETranslation,
    },
    'es-UY': {
        translation: esUYTranslation,
    },
    'es-PE': {
        translation: esPETranslation,
    },
    'pt-BR': {
        translation: ptBRTranslation,
    },
    'pt-PT': {
        translation: ptPTTranslation,
    },
    'it-IT': {
        translation: itITTranslation,
    },
    'de-DE': {
        translation: deDETranslation,
    },
    'fr-FR': {
        translation: frFRTranslation,
    },
    'en-UK': {
        translation: enUKTranslation,
    },
    'en-GB': {
        translation: enUKTranslation,
    },
    'zh-CN': {
        translation: zhCNTranslation,
    },
    'hi-IN': {
        translation: hiINTranslation,
    },
    'ko-KR': {
        translation: koKRTranslation,
    },
    'ja-JP': {
        translation: jaJPTranslation,
    },
    'zh-TW': {
        translation: zhTWTranslation,
    },
    'ar-SA': {
        translation: arSATranslation,
    },
    'ar-AE': {
        translation: arAETranslation,
    },
    'en-SG': {
        translation: enSGTranslation,
    },
}

i18n.use(initReactI18next).init({
    resources,
    fallbackLng: 'es-AR', // Default language fallback
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
        escapeValue: false, // React already escapes strings
    },
})

const generateUUIDFromDatetime = async () => {
    // Get the current time as a string
    const currentTime = new Date().toISOString()
    // Generate a hash using the SubtleCrypto API
    const hashBuffer = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(currentTime)
    )
    // Convert the hash to a hexadecimal string
    const hexString = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
    // Use the first 32 characters for the UUID (similar to UUID v4 format)
    return `${hexString.substring(0, 8)}-${hexString.substring(
        8,
        12
    )}-${hexString.substring(12, 16)}-${hexString.substring(
        16,
        20
    )}-${hexString.substring(20, 32)}`
}

type StepWithMeta = {
    selector?: string
    content: React.ReactNode | ((args: any) => React.ReactNode)
    forward?: () => void
    meta?: { onClose?: () => void }
}

export const wrapPageElement = ({ element }) => {
    const { i18n } = useTranslation()

    useEffect(() => {
        const storedLocale = getStoredLocale()
        const browserLocale = resolveAcceptedLocale(window?.navigator?.language)
        i18n.changeLanguage(storedLocale || browserLocale)
        const deviceId = localStorage.getItem('deviceId')
        if (!deviceId) {
            generateUUIDFromDatetime().then((value) => {
                createDevice({ id: value }).then((data) => {
                    if (data?.id == value) {
                        localStorage.setItem('deviceId', value)
                    }
                })
            })
        }
    }, [])

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <TourProvider
                    steps={[]}
                    scrollSmooth={true}
                    showDots={false}
                    showCloseButton={true}
                    components={{ Content: AnnaTourContent }}
                    onClickClose={({ steps, currentStep, setIsOpen }) => {
                        const s = (steps as StepWithMeta[] | undefined)?.[
                            currentStep
                        ]
                        s?.meta?.onClose?.()
                        setIsOpen(false)
                    }}
                    // 2) Mask click (outside the popover)
                    onClickMask={({ steps, currentStep, setIsOpen }) => {
                        const s = (steps as StepWithMeta[] | undefined)?.[
                            currentStep
                        ]
                        s?.meta?.onClose?.()
                        setIsOpen(false)
                    }}
                    // Optional: prevent Esc from closing so your logic always runs via the handlers above
                    showNavigation={true}
                    showPrevNextButtons={true}
                    showBadge={false}
                    disableKeyboardNavigation={true}
                    styles={tourStyles(theme)}
                    nextButton={({
                        currentStep,
                        stepsLength,
                        setCurrentStep,
                        setIsOpen,
                    }) => {
                        const isLastStep = currentStep === stepsLength - 1

                        if (isLastStep) {
                            return (
                                <IconButton
                                    color={'primary'}
                                    onClick={() => {
                                        setIsOpen(false)
                                        setCurrentStep(currentStep + 1)
                                    }}
                                >
                                    <DoneIcon />
                                </IconButton>
                            )
                        }

                        return (
                            <IconButton
                                color={'primary'}
                                onClick={() => setCurrentStep(currentStep + 1)}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        )
                    }}
                    prevButton={({ currentStep, setCurrentStep }) => {
                        const isFirstStep = currentStep === 0
                        if (isFirstStep) return <Box></Box>

                        return (
                            <IconButton
                                color={'primary'}
                                onClick={() => setCurrentStep(currentStep - 1)}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        )
                    }}
                >
                    {element}
                </TourProvider>
            </ThemeProvider>
        </React.StrictMode>
    )
}

export const wrapRootElement = ({ element, props }) => {
    return (
        <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>{element}</I18nextProvider>
        </QueryClientProvider>
    )
}

export const onServiceWorkerUpdateReady = () => {
    // const { t } = useTranslation()

    // const answer = window.confirm(
    //     t(
    //         'This application has been updated. ' +
    //             'Reload to display the latest version?'
    //     )
    // )

    // if (answer === true) {
    window.location.reload()
    // }
}
